import {
  type AttendanceDay,
  type AttendanceRecord,
  type AttendanceRecordList,
  type AttendanceRequest,
  attendanceDaySchema,
  attendanceRecordListSchema,
  attendanceRecordSchema,
  attendanceRequestListSchema,
  attendanceRequestSchema,
  type Checkin,
  type CheckinLogType,
  type CreateRequestInput,
  cancelledRequestSchema,
  checkinListSchema,
  checkinSchema,
  type DecideResult,
  decideResultSchema,
  type LastCheckin,
  lastCheckinSchema,
  type MyDay,
  myDaySchema,
  type Presence,
  presenceSchema,
} from "@/features/attendance/schemas/attendance.schema";
import {
  type CancelResult,
  cancelResultSchema,
  type Holiday,
  holidayListSchema,
} from "@/features/attendance/schemas/attendance-config.schema";
import {
  type AttendanceRegister,
  attendanceRegisterSchema,
} from "@/features/attendance/schemas/attendance-register.schema";
import { apiCall } from "@/shared/lib/api-call";
import { apiPaths } from "@/shared/lib/api-paths";
import { downloadFile } from "@/shared/lib/download-file";

/// HTTP boundary for M16 attendance. Every response is Zod-parsed through the
/// shared `apiCall` helper (webui T3 / FE-SEC6).

function qs(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, value);
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

export function downloadAttendanceRegister(
  workspaceId: string,
  filters: { month: string; departmentId?: string },
): Promise<void> {
  return downloadFile(
    `${apiPaths.attendance.registerExportCsv(workspaceId)}${qs(filters)}`,
    `attendance-register-${filters.month}.csv`,
  );
}

export async function listAttendanceRegister(
  workspaceId: string,
  filters: { month: string; departmentId?: string },
): Promise<AttendanceRegister> {
  return apiCall<AttendanceRegister>(
    `${apiPaths.attendance.register(workspaceId)}${qs(filters)}`,
    { method: "GET" },
    attendanceRegisterSchema,
  );
}

/// The caller's own month. The server pins this to the caller regardless of
/// any filter, so there is no membershipId to pass.
export async function listMyAttendance(
  workspaceId: string,
  range: { from: string; to: string },
): Promise<AttendanceRecordList> {
  return apiCall<AttendanceRecordList>(
    `${apiPaths.attendance.myRecords(workspaceId)}${qs(range)}`,
    { method: "GET" },
    attendanceRecordListSchema,
  );
}

/// Records across whoever the caller may see. The server narrows this to their
/// own row, their subtree, or the workspace, based on permissions alone.
export async function listAttendance(
  workspaceId: string,
  filters: {
    from?: string;
    to?: string;
    membershipId?: string;
    departmentId?: string;
    status?: string;
  },
): Promise<AttendanceRecordList> {
  return apiCall<AttendanceRecordList>(
    `${apiPaths.attendance.records(workspaceId)}${qs(filters)}`,
    { method: "GET" },
    attendanceRecordListSchema,
  );
}

/// The day's records AND the people in scope who have none. See
/// `unrecordedMemberSchema` for why the blanks are half the answer.
export async function listDay(workspaceId: string, date: string): Promise<AttendanceDay> {
  return apiCall<AttendanceDay>(
    `${apiPaths.attendance.day(workspaceId)}${qs({ date })}`,
    { method: "GET" },
    attendanceDaySchema,
  );
}

/// The caller's own schedule for a date. Self-scoped server-side, so there is
/// no membership to pass and no way to point it at a colleague.
export async function getMyDay(workspaceId: string, date: string): Promise<MyDay> {
  return apiCall<MyDay>(
    `${apiPaths.attendance.myDay(workspaceId)}${qs({ date })}`,
    { method: "GET" },
    myDaySchema,
  );
}

export async function getLastCheckin(workspaceId: string): Promise<LastCheckin> {
  return apiCall<LastCheckin>(
    apiPaths.attendance.lastCheckin(workspaceId),
    { method: "GET" },
    lastCheckinSchema,
  );
}

/// Record a tap. `logType` is omitted deliberately: the server decides the
/// direction from the last log, so a stale client cannot write two arrivals
/// in a row.
export async function recordCheckin(
  workspaceId: string,
  input?: { logType?: CheckinLogType; time?: string; localTime?: string },
): Promise<Checkin> {
  return apiCall<Checkin>(
    apiPaths.attendance.checkins(workspaceId),
    { method: "POST", body: JSON.stringify(input ?? {}) },
    checkinSchema,
  );
}

export async function listCheckins(
  workspaceId: string,
  filters: { from?: string; to?: string } = {},
): Promise<{ items: Checkin[] }> {
  return apiCall<{ items: Checkin[] }>(
    `${apiPaths.attendance.checkins(workspaceId)}${qs(filters)}`,
    { method: "GET" },
    checkinListSchema,
  );
}

export async function createAttendanceRequest(
  workspaceId: string,
  input: CreateRequestInput,
): Promise<AttendanceRequest> {
  return apiCall<AttendanceRequest>(
    apiPaths.attendance.requests(workspaceId),
    { method: "POST", body: JSON.stringify(input) },
    attendanceRequestSchema,
  );
}

export async function listAttendanceRequests(
  workspaceId: string,
  filters: { scope?: "mine" | "to-decide"; status?: string } = {},
): Promise<{ items: AttendanceRequest[] }> {
  return apiCall<{ items: AttendanceRequest[] }>(
    `${apiPaths.attendance.requests(workspaceId)}${qs(filters)}`,
    { method: "GET" },
    attendanceRequestListSchema,
  );
}

export async function approveAttendanceRequest(
  workspaceId: string,
  requestId: string,
  note?: string,
): Promise<DecideResult> {
  return apiCall<DecideResult>(
    apiPaths.attendance.approveRequest(workspaceId, requestId),
    { method: "POST", body: JSON.stringify(note ? { note } : {}) },
    decideResultSchema,
  );
}

export async function rejectAttendanceRequest(
  workspaceId: string,
  requestId: string,
  note?: string,
): Promise<AttendanceRequest> {
  return apiCall<AttendanceRequest>(
    apiPaths.attendance.rejectRequest(workspaceId, requestId),
    { method: "POST", body: JSON.stringify(note ? { note } : {}) },
    attendanceRequestSchema,
  );
}

/// Record a day by hand. Only three paths ever write a verdict: the engine,
/// an approved request, and an admin using this (stories GUARD-3).
export async function markAttendance(
  workspaceId: string,
  input: {
    membershipId: string;
    attendanceDate: string;
    status: string;
    halfDayStatus?: string;
    workedHalfDayPeriod?: "first_half" | "second_half";
  },
): Promise<AttendanceRecord> {
  return apiCall<AttendanceRecord>(
    apiPaths.attendance.records(workspaceId),
    { method: "POST", body: JSON.stringify(input) },
    attendanceRecordSchema,
  );
}

export async function correctAttendance(
  workspaceId: string,
  attendanceId: string,
  input: {
    status?: string;
    halfDayStatus?: string | null;
    workedHalfDayPeriod?: "first_half" | "second_half" | null;
    reason?: string;
    expectedVersion: number;
  },
): Promise<AttendanceRecord> {
  return apiCall<AttendanceRecord>(
    apiPaths.attendance.record(workspaceId, attendanceId),
    { method: "PATCH", body: JSON.stringify(input) },
    attendanceRecordSchema,
  );
}

/// Cancel a verdict. Releases the check-ins it was derived from, so the engine
/// can reconsider them, and frees the date for a replacement.
export async function cancelAttendance(
  workspaceId: string,
  attendanceId: string,
): Promise<CancelResult> {
  return apiCall<CancelResult>(
    apiPaths.attendance.cancelRecord(workspaceId, attendanceId),
    { method: "POST" },
    cancelResultSchema,
  );
}

export async function cancelAttendanceRequest(
  workspaceId: string,
  requestId: string,
): Promise<{ cancelled: true; attendanceCancelled: number }> {
  return apiCall(
    apiPaths.attendance.cancelRequest(workspaceId, requestId),
    { method: "POST" },
    cancelledRequestSchema,
  );
}

/// The requester withdrawing their own pending request. Distinct from
/// `cancelAttendanceRequest` above: that one hits the approver-cancel
/// endpoint, gated on a decide permission, and returns a different shape.
export async function withdrawAttendanceRequest(
  workspaceId: string,
  requestId: string,
): Promise<AttendanceRequest> {
  return apiCall<AttendanceRequest>(
    apiPaths.attendance.withdrawRequest(workspaceId, requestId),
    { method: "POST" },
    attendanceRequestSchema,
  );
}

/// Who in the caller's scope is checked in right now. Breadth is decided by
/// the server, like every other team read.
export async function getPresence(workspaceId: string): Promise<Presence> {
  return apiCall<Presence>(
    apiPaths.attendance.presence(workspaceId),
    { method: "GET" },
    presenceSchema,
  );
}

/// Non-working days for the caller's own calendar, for the month grid. With
/// `membershipId`, that person's calendar instead (empty if out of scope).
export async function listHolidays(
  workspaceId: string,
  range: { from: string; to: string; membershipId?: string },
): Promise<{ items: Holiday[] }> {
  return apiCall<{ items: Holiday[] }>(
    `${apiPaths.attendance.holidays(workspaceId)}${qs(range)}`,
    { method: "GET" },
    holidayListSchema,
  );
}

export type { AttendanceRecord };
